import { Injectable } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Observable, OperatorFunction } from 'rxjs';
import { SubmissionFormOptions } from '../domain/submission-form';
import { FlatFormGroup, FormGroupValue } from '../domain/typed-form';
import { InferredSubmissionForm } from '../forms/inferred-submission-form';
import { SubmissionForm } from '../forms/submission-form';

const defaultOptions: SubmissionFormOptions = {
  initialValueConfig: { emitEvent: false },
  dirtyCheckConfig: { enabled: true, useBeforeunloadEvent: false },
};

@Injectable({
  providedIn: 'root',
})
export class SubmissionFormFactoryService {
  public constructor(public readonly fb: NonNullableFormBuilder) {}

  /**
   * Use this when you build your form using the FlatFormGroup helper with an interface
   * This can also be used when you want to use a form that adheres to the FlatFormGroup rules
   * @param form FlatFormGroup or FormGroup instance
   * @param submitOperator rxjs operator to submit the form
   * @param initialValue the intial value of the form
   * @returns SubmissionForm instance
   */
  public createSubmissionForm<T, TResult>(
    form: FlatFormGroup<T>,
    submitOperator: OperatorFunction<T, TResult>,
    initialValue: Partial<T> | Observable<Partial<T>> = {},
    options: Partial<SubmissionFormOptions> = defaultOptions,
  ): SubmissionForm<T, TResult> {
    return new SubmissionForm<T, TResult>(form, submitOperator, initialValue, this.getOptions(options));
  }

  /**
   * Use this when you build your form using inbuilt angular forms without specifying an interface
   * The interface / value of the form will be inferred
   * @param form FlatFormGroup or FormGroup instance
   * @param submitOperator rxjs operator to submit the form
   * @param initialValue the intial value of the form
   * @returns SubmissionForm instance
   */
  public createInferredSubmissionForm<TForm extends FormGroup, TResult>(
    form: TForm,
    submitOperator: OperatorFunction<FormGroupValue<TForm>, TResult>,
    initialValue: Partial<FormGroupValue<TForm>> | Observable<Partial<FormGroupValue<TForm>>> = {},
    options: Partial<SubmissionFormOptions> = defaultOptions,
  ): InferredSubmissionForm<TForm, TResult> {
    return new InferredSubmissionForm<TForm, TResult>(form, submitOperator, initialValue, this.getOptions(options));
  }

  private getOptions(options: Partial<SubmissionFormOptions>): SubmissionFormOptions {
    return {
      initialValueConfig: {
        ...defaultOptions.initialValueConfig,
        ...options.initialValueConfig,
      },
      dirtyCheckConfig: {
        ...defaultOptions.dirtyCheckConfig,
        ...options.dirtyCheckConfig,
      },
    };
  }
}
